import { MatDialogConfig, DialogPosition } from '@angular/material/dialog';
import { ElementRef } from '@angular/core';
import * as units from './units';
import * as settings from './settings';
import * as enums from './enums';
import * as utm from './utm-latlng';
import * as uuid from 'uuid';
import { Point } from '../common/Point';

export var _ = require('lodash');

utm.init(undefined);

var nationToFlag = new Map<string, string>();

function degToDMS(coord: number): string {
  let degFull = Math.floor(coord);
  let min = (coord - degFull) * 60;
  let minFull = Math.floor(min);
  let sec = (min - minFull) * 60;
  let secStr = sec.toFixed(1);
  if (Number(secStr) == 60) {
    sec = 0;
    minFull++;
    if (minFull == 60) {
      minFull = 0;
      degFull++;
    }
  }
  return `${degFull.toString().padStart(2, '0')}${units.DEGREE_CHAR}${minFull.toString().padStart(2, '0')}'${sec.toFixed(1).padStart(4, '0')}"`;
}

function degFromDMS(dms: string): number {
  let strings = dms.split(RegExp(`[${units.DEGREE_CHAR}'"]`), 3);
  let deg = Number(strings[0]);
  let min = Number(strings[1]);
  let sec = Number(strings[2]);
  return deg + min / 60 + sec / 3600;
}

export function getCoordinatesString(coord: Point, coordType?: enums.CoordinateType, radians?: boolean): string {
  let actualCoordType = typeof coordType == 'undefined' ? settings.userSettings.coordinatesType : coordType;
  let strings = getCoordinatesStrings(coord, actualCoordType, radians);
  if (strings.length > 0) {
    if (actualCoordType == enums.CoordinateType.WGS84_DMM || actualCoordType == enums.CoordinateType.WGS84_DMS) {
      return `${strings[0]} , ${strings[1]}`;
    }
    else if (actualCoordType == enums.CoordinateType.UTM) {
      return `${strings[0]} ${strings[1]} ${strings[2]}`;
    }
  }
  return ``;
}

export function getCoordinatesStrings(coord: Point, coordType?: enums.CoordinateType, radians?: boolean): string[] {
  let actualCoordType = typeof coordType == 'undefined' ? settings.userSettings.coordinatesType : coordType;
  let locX: number = radians ? coord.x * units.RAD_TO_DEG : coord.x;
  let locY: number = radians ? coord.y * units.RAD_TO_DEG : coord.y;
  if (locX >= -180 && locX <= 180 && locY >= -90 && locY <= 90) {
    if (actualCoordType == enums.CoordinateType.WGS84_DMM || actualCoordType == enums.CoordinateType.WGS84_DMS) {
      let north = locY > 0 ? 'N' : 'S';
      let east = locX > 0 ? 'E' : 'W';
      if (actualCoordType == enums.CoordinateType.WGS84_DMM) {
        // N 32.0055 deg , E 34.8854 deg
        return [`${north} ${Math.abs(locY).toFixed(4).padStart(7, '0')}${units.DEGREE_CHAR}`,
                `${east} ${Math.abs(locX).toFixed(4).padStart(7, '0')}${units.DEGREE_CHAR}`];
      }
      else {
        // N 32 deg 10 min 03 sec , E 34 deg 06 min 12 sec
        return [`${north} ${degToDMS(Math.abs(locY))}`,
                `${east} ${degToDMS(Math.abs(locX))}`];
      }
    }
    else if (actualCoordType == enums.CoordinateType.UTM) {
      let utmCoord = utm.convertLatLngToUtm(locY, locX, 1);
      if (utmCoord)
        return [`${utmCoord.ZoneNumber.toString().padStart(2, '0')}${utmCoord.ZoneLetter}`,
                `${Math.round(utmCoord.Easting).toString().padStart(6, '0')}`,
                `${Math.round(utmCoord.Northing).toString().padStart(7, '0') }`];
    }
  }
  return [];
}

export function getCoordinatesFromString(coordStr: string, coordType?: enums.CoordinateType): Point {
  let actualCoordType = typeof coordType == 'undefined' ? settings.userSettings.coordinatesType : coordType;
  let strings = [];
  if (actualCoordType == enums.CoordinateType.WGS84_DMM || actualCoordType == enums.CoordinateType.WGS84_DMS) {
    strings = coordStr.split(' , ');
  }
  else if (actualCoordType == enums.CoordinateType.UTM) {
    strings = coordStr.split(' ');
  }
  if (strings.length > 0)
    return getCoordinatesFromStrings(strings, actualCoordType);
}

export function getCoordinatesFromStrings(coordStr: string[], coordType?: enums.CoordinateType): Point {
  let actualCoordType = typeof coordType == 'undefined' ? settings.userSettings.coordinatesType : coordType;
  if (actualCoordType == enums.CoordinateType.WGS84_DMM || actualCoordType == enums.CoordinateType.WGS84_DMS) {
    let yStr = coordStr[0].split(' ');
    let xStr = coordStr[1].split(' ');
    let y: number;
    let x: number;
    if (actualCoordType == enums.CoordinateType.WGS84_DMM) {
      y = Number(yStr[1].slice(0, yStr[1].length - 1));
      x = Number(xStr[1].slice(0, xStr[1].length - 1));
    }
    else {
      y = degFromDMS(yStr[1]);
      x = degFromDMS(xStr[1]);
    }
    if (yStr[0].toUpperCase() == 'S')
      y *= -1;
    if (xStr[0].toUpperCase() == 'W')
      x *= -1;
    return { x, y };
  }
  else if (actualCoordType == enums.CoordinateType.UTM) {
    let easting = Number(coordStr[1]);
    let northing = Number(coordStr[2]);
    let zoneNumber = Number(coordStr[0].slice(0, coordStr[0].length - 1));
    let zoneLetter = coordStr[0].slice(coordStr[0].length - 1 , coordStr[0].length);
    let ll = utm.convertUtmToLatLng(easting, northing, zoneNumber, zoneLetter);
    if (ll)
      return { x: ll.lng, y: ll.lat };
  }
  return null;
}

export function modeC2Height(alt: number): number {
  let meters = (alt - 13) * 100 * units.FEET_TO_METER;
  return meters;
}

export function height2ModeC(alt: number): number {
  let feet = alt / units.FEET_TO_METER;
  let modeC = Math.round((feet + 1200.) / 100. + 1);
  return modeC;
}

export function getIFFString(iff: number) {
  return iff.toString(8).padStart(4, '0');
}

// convert NeWMI color [r, g, b] (values 0-1) to rgb(r,g,b) or rgba(r,g,b,a)
export function newmiColorToString(color: number[]): string {
  let r = Math.round(color[0] * 255.0);
  let g = Math.round(color[1] * 255.0);
  let b = Math.round(color[2] * 255.0);
  if (color.length == 3) {
    return `rgb(${r},${g},${b})`;
  }
  else {
    let a = color[3];
    return `rgb(${r},${g},${b},${a})`;
  }
}

export function cssColorToNewmi(color: string): number[] {
  return NeWMI.Draw.Draw2D.getCSSColorValues(color, true) as number[];
}

export function distanceMeterToDeg(origin, distMeter: number, horizontal: boolean = true): number {
  if (distMeter == 0)
    return 0;
  
  // From NeWMI documentation:
  // The function perform coarse conversion between distance measured in meters to decimal degrees (geographic projection units).
  // The conversion has accuracy around 3 meters for every kilometer of distance.
  let distDeg = NeWMI.Service.Math.ConversionsSvc.simpleMetersToDegreesConversion(distMeter, origin.x, origin.y, horizontal);
  return distDeg;
}

export function distanceDegToMeter(origin, distDeg: number, horizontal: boolean = true): number {
  if (distDeg == 0)
    return 0;
  
  // From NeWMI documentation:
  // The function perform coarse conversion between distance measured in decimal degrees (geographic projection units) to meters.
  // The conversion has accuracy around 3 meters for every kilometer of distance.
  let distMeters = NeWMI.Service.Math.ConversionsSvc.simpleDegreesToMetersConversion(distDeg, origin.x, origin.y, horizontal);
  return distMeters;
}

export function newmiPointByRangeAzimuth(origin, range, azimuth, verticalRange) {
  let lat1 = origin.y * units.DEG_TO_RAD;
  let lon1 = origin.x * units.DEG_TO_RAD;
  const azRad = azimuth * units.DEG_TO_RAD;

  let rangeRad = range > 0 ? distanceMeterToDeg(origin, range) * units.DEG_TO_RAD : 0;

  let lat2 = Math.asin(Math.sin(lat1) * Math.cos(rangeRad) +
              Math.cos(lat1) * Math.sin(rangeRad) * Math.cos(azRad));
  let lon2 = lon1 + Math.atan2(Math.sin(azRad) * Math.sin(rangeRad) * Math.cos(lat1),
                                Math.cos(rangeRad) - Math.sin(lat1) * Math.sin(lat2));

  return new NeWMI.Geometry.Point({ x: lon2 * units.RAD_TO_DEG, y: lat2 * units.RAD_TO_DEG, z: origin.z + verticalRange });
}

export function newmiPointByRangeAzimuthElevation(origin, range, azimuth, elevation) {
  // calculate vertical range
  let verticalRange = range * Math.tan(elevation);
  return newmiPointByRangeAzimuth(origin, range, azimuth, verticalRange);
}

// Return result in meters
export function newmiDistnace(from, to) {
  let distDeg = NeWMI.Service.Math.MeasurementSvc.distancePnts(from, to);
  return distanceDegToMeter(from, distDeg);
}

export function newmiHeading(from, to) {
  let hdg = NeWMI.Service.Math.MeasurementSvc.getAngle(from.x, from.y, to.x, to.y);
  return hdg;
}

export function newmiDarkerColor(color, factor) {
  if (factor > 1)
    factor = 1;
  else if (factor < 0)
    factor = 0;
  let r = color[0] * factor;
  let g = color[1] * factor;
  let b = color[2] * factor;
  if (color.length == 4) {
    let a = color[3];
    return [r, g, b, a];
  }
  else {
    return [r, g, b];
  }
}

export function newmiLighterColor(color, factor) {
  factor = 1. + factor;
  if (factor < 1)
    factor = 1;
  let r = color[0] * factor;
  if (r > 1) r = 1;
  let g = color[1] * factor;
  if (g > 1) g = 1;
  let b = color[2] * factor;
  if (b > 1) b = 1;
  if (color.length == 4) {
    let a = color[3];
    return [r, g, b, a];
  }
  else {
    return [r, g, b];
  }
}

export function newmiAlphaColor(color, alpha) {
  let r = color[0];
  let g = color[1];
  let b = color[2];
  let a = alpha;
  return [r, g, b, a];
}

export function isWebGLSupported(): boolean {
  return NeWMI.Pure.Services.Draw.isWebGLSupported();
}

export function createDialogConfig(resizable: boolean = true, modal: boolean = false, id?: string, position?: DialogPosition): MatDialogConfig {
  let dialogConfig = new MatDialogConfig();
  dialogConfig.autoFocus = true;
  dialogConfig.hasBackdrop = modal;
  if (resizable)
    dialogConfig.panelClass = 'resizable-dialog';
  if (id)
    dialogConfig.id = id;
  dialogConfig.position = position ? position : { top: '100px', left: '100px' };
  return dialogConfig;
}

// Return true if collections are equal
export function isEqual(a, b): boolean {
  let eq = _.isEqual(a, b);
  return eq;
}

function pxStrToNum(str): number {
  return Number.parseInt(str.substr(0, str.length - 2));
}

// Return {x,y} of CDK overlay element
export function getCdkOverlayPosition(element: ElementRef) {
  let elem = element.nativeElement;
  let pos = { x: 0, y: 0 };
  while (elem) {
    if (elem.className == 'cdk-overlay-pane') {
      let transform: string = elem.style.transform;
      if (transform.startsWith('translate3d(')) {
        let from = transform.indexOf('(');
        let to = transform.indexOf(')', from);
        let substr = transform.slice(from + 1, to);
        let split = substr.split(', ');
        let xPx = split[0];
        let yPx = split[1];
        let xOffset = pxStrToNum(xPx);
        let yOffset = pxStrToNum(yPx);
        from = transform.indexOf('(', to);
        to = transform.indexOf(')', from);
        substr = transform.slice(from + 1, to);
        let x = pxStrToNum(substr);
        from = transform.indexOf('(', to);
        to = transform.indexOf(')', from);
        substr = transform.slice(from + 1, to);
        let y = pxStrToNum(substr);
        pos.x = x + xOffset;
        pos.y = y + yOffset;
      }
      break;
    }
    elem = elem.parentElement;
  }
  return pos;
}

// Get image URL of nationality (represented as 3 letter code)
export function getNationalityImage(nationality: string): string {
  if (nationToFlag.size == 0) {
    nationToFlag.set("ISR", "israel");
    nationToFlag.set("EGY", "egypt");
    nationToFlag.set("SYR", "syria");
    nationToFlag.set("LBN", "lebanon");
    nationToFlag.set("IRN", "iran");
    nationToFlag.set("IRQ", "iraq");
    nationToFlag.set("USA", "united-states-of-america");
    nationToFlag.set("GBR", "united-kingdom");
    nationToFlag.set("GBN", "united-kingdom");
    nationToFlag.set("ITA", "italy");
    nationToFlag.set("FRA", "france");
  }

  if (nationToFlag.has(nationality))
    return `url('../../assets/Images/Flags/${nationToFlag.get(nationality)}.svg')`;
  else
    return null;
}

// Get image URL of plane symbol
export function getPlaneSymbol(symbol: string): string {
  return `url('../../assets/Images/Objects/${symbol}.svg')`;
}

// Generate UUID
export function generateUuid(): string {
  return uuid.v4();
}

// Calculate pitch from horizontal and vertical speed
export function speedToPitch(horizSpeed: number, vertSpeed: number) {
  let pitch = Math.atan2(vertSpeed, horizSpeed);
  return pitch;
}

export function capitalizedFirstKey(key: string){
  return key.length ? key[0].toUpperCase() + key.slice(1): key;
}

export function KeysFirstLetterUppercase<T extends any>(value: T): any{
  if (Array.isArray(value)) return value.map(KeysFirstLetterUppercase);

  if (value && typeof(value) == 'object') {
    const out : any = {};
    for (const [k,v] of Object.entries(value)){
      out[capitalizedFirstKey(k)] = KeysFirstLetterUppercase(v);
    }
    return out;
  }

  return value;
}

export function extractSensorAcousticName(name: string): any {
  const parts = name.split('_');

  for (let i = parts.length - 1; i >= 0; i--){
    if (parts[i].length == 4 && parts[i].startsWith('202')&& /^\d+$/.test(parts[i])){
      return parts.slice(0,i).join('_');
    }
  } 
  return name;
}
